import {
  createFilterDTOSchema,
  createPageQueryDTOSchema,
  DEFAULT_PAGE_LIMIT,
  timestampDTOSchema,
} from '@tempo-io/common-dto';
import { z } from 'zod';

export const organizationNameSchema = z
  .string()
  .nonempty('Organization URL is required')
  .regex(/^[a-z0-9-]+$/, {
    message: 'Organization URL must contain only lowercase letters, numbers and hyphens.',
  })
  .max(50, 'The custom URL path must be 50 characters or less');

const organizationDisplayNameSchema = z
  .string()
  .nonempty('Organization name is required')
  .max(255, 'Organization name can be a maximum of 255 characters long');

export const organizationDTOSchema = z.object({
  id: z.string().nonempty(),
  auth0Id: z.string().nonempty(),
  name: organizationNameSchema,
  displayName: organizationDisplayNameSchema,
  monetizeNowAccountId: z.string().nullable(),
  ...timestampDTOSchema.shape,
});

export const organizationArrayDTOSchema = z.array(organizationDTOSchema);

export const organizationCreateDTOSchema = z.object({
  name: organizationNameSchema,
  displayName: organizationDisplayNameSchema,
});

export const organizationConnectionsDTOSchema = z.object({
  organizationId: z.string().nonempty(),
  connections: z.array(
    z.object({
      strategy: z.string(),
      name: z.string(),
    }),
  ),
});

export const organizationConnectionsArrayDTOSchema = z.array(organizationConnectionsDTOSchema);

export const organizationUpdateDTOSchema = z
  .object({
    displayName: organizationDisplayNameSchema.optional(),
    name: organizationNameSchema.optional(),
  })
  .refine((data) => data.displayName !== undefined || data.name !== undefined, {
    message: 'At least one of displayName or name must be provided',
  });

export const organizationSortableFields = ['id', 'name', 'createdAt', 'updatedAt'] as const;

export const { filterSchema: organizationFilterSchema } = createFilterDTOSchema({
  updatedAt: 'date',
});

export const organizationSearchInputDTOSchema = z
  .object({
    filter: organizationFilterSchema,
    ...createPageQueryDTOSchema({
      sortableFields: organizationSortableFields,
      defaultSortBy: 'createdAt',
    }).shape,
  })
  .optional()
  .default({ sortOrder: 'asc', sortBy: 'createdAt', limit: DEFAULT_PAGE_LIMIT });
